import { useInfiniteQuery, type InfiniteData, type QueryKey, type UseInfiniteQueryResult } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';

export const DEFAULT_PAGE_SIZE = 100;

type RecordPage<TItem> = { items: TItem[]; hasMore: boolean };

type UseCompleteRecordListResult<TItem> = {
  items: TItem[];
  query: UseInfiniteQueryResult<InfiniteData<RecordPage<TItem>, number>, Error>;
  /** `true` once every page has been fetched — i.e. `items` holds the full result set. */
  isComplete: boolean;
};

type UseCompleteRecordListArgs<TItem> = {
  /**
   * The query key for this list. Build it from the tRPC `.list.queryKey({...})` helper so it stays
   * prefix-compatible with `queryFilter` invalidation. The tRPC `infiniteQueryKey` helper can't be
   * used here — these procedures paginate by `offset`, not a `cursor` field.
   */
  queryKey: QueryKey;
  /** Fetch one page. Adapt the tRPC response into `{ items, hasMore }` here. */
  queryFn: (params: { offset: number; count: number }) => Promise<RecordPage<TItem>>;
  pageSize?: number;
  enabled?: boolean;
};

/**
 * Fetches an offset-paginated record list *in full* — pages through every result and returns them
 * flattened. Use for views that need the complete set rather than a scroll window (e.g. a timeline
 * that derives its date range and prev/next navigation from all records, where a partial fetch would
 * silently drop older entries). For scroll-on-demand lists use `useInfiniteRecordList` instead.
 */
export const useCompleteRecordList = <TItem>({
  queryKey,
  queryFn,
  pageSize = DEFAULT_PAGE_SIZE,
  enabled,
}: UseCompleteRecordListArgs<TItem>): UseCompleteRecordListResult<TItem> => {
  // Pin the generics — notably the `number` page-param type — so the inferred result type matches
  // the annotated return type instead of widening the page param to `unknown`.
  const query = useInfiniteQuery<RecordPage<TItem>, Error, InfiniteData<RecordPage<TItem>, number>, QueryKey, number>({
    queryKey,
    queryFn: async ({ pageParam }) => queryFn({ offset: pageParam, count: pageSize }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, _allPages, lastOffset) => (lastPage.hasMore ? lastOffset + pageSize : undefined),
    enabled,
  });

  const pages = query.data?.pages;
  const items = useMemo(() => pages?.flatMap((page) => page.items) ?? [], [pages]);

  // Keep fetching until the server is exhausted so the full set is available. Re-running on `pages`
  // after each page settles chains the requests one after another.
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = query;
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) {
      void fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, pages]);

  return { items, query, isComplete: query.isSuccess && !hasNextPage };
};
